/**
 * Turns a Knowledge Base document (a Word paste run through readableMarkdown
 * and plainTextToMarkdown) into something that reads like a document in a
 * lesson: the short cover lines at the top come apart from the body, and
 * headings typed in ALL CAPS get title case.
 */

const KEEP_UPPER = new Set([
  "AI", "AEO", "API", "B2B", "B2C", "CPA", "CPC", "CPL", "CRM", "CTM", "CTR", "FAQ", "GA4", "GBP", "GEO", "GHL", "GMB",
  "GTM", "KB", "KPI", "KPIS", "LLM", "LLMS", "LSA", "LTV", "NEPQ", "OS", "PPC", "ROAS", "ROI", "SEO", "SMS", "SOP", "SOPS",
  "UTM", "USP", "US", "USA", "YOY", "HVAC", "PR", "UI", "UX", "CTA", "CTAS", "NL", "AOS", "CRO", "GML", "MCP",
]);
/** Names that keep their capitals in a sentence-case label. */
const PROPER: Record<string, string> = { GOOGLE: "Google", META: "Meta", MICROSOFT: "Microsoft", LINKEDIN: "LinkedIn", TIKTOK: "TikTok", YOUTUBE: "YouTube" };
const SMALL = new Set(["a", "an", "and", "as", "at", "but", "by", "for", "in", "nor", "of", "on", "or", "the", "to", "vs", "via", "with"]);

/** Re-cases ALL-CAPS text word by word; acronyms ("SEO", "GA4") keep their capitals. Mixed case is left alone. */
function recaseShouting(text: string, other: (word: string, first: boolean) => string): string {
  const letters = text.replace(/[^A-Za-z]/g, "");
  if (letters.length < 4 || letters !== letters.toUpperCase()) return text;
  let first = true;
  return text.replace(/[A-Za-z][A-Za-z0-9'’]*/g, (word) => {
    const upper = word.toUpperCase();
    const bare = upper.replace(/['’]S$/, "");
    const out = KEEP_UPPER.has(bare) || /\d/.test(word)
      ? (word.length > bare.length ? bare + word.slice(bare.length).toLowerCase() : upper)
      : other(word, first);
    first = false;
    return out;
  });
}

const capitalise = (word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/** "WHAT NO LIMITS MARKETING AGENCY IS" → "What No Limits Marketing Agency Is"; mixed case is left alone. */
export function titleCaseShouting(text: string): string {
  return recaseShouting(text, (word, first) => (!first && SMALL.has(word.toLowerCase()) ? word.toLowerCase() : capitalise(word)));
}

/** "AI SEARCH & GEO" → "AI search & GEO", "MICROSOFT ADVERTISING" → "Microsoft advertising": a label in sentence case. */
export function sentenceCaseShouting(text: string): string {
  return recaseShouting(text, (word, first) => {
    const proper = PROPER[word.toUpperCase().replace(/['’]S$/, "")];
    if (proper) return proper + word.slice(proper.length).toLowerCase();
    return first ? capitalise(word) : word.toLowerCase();
  });
}

/** Title-cases every ALL-CAPS markdown heading. */
export function calmHeadings(md: string): string {
  return md.replace(/^(#{1,6})[ \t]+(.+)$/gm, (_, hashes: string, text: string) => `${hashes} ${titleCaseShouting(text.trim())}`);
}

/** A document's own cover: a small label, its cover title, the lines under it, and its category line. */
export type DocCover = { eyebrow: string | null; title: string | null; lines: string[]; meta: string | null };

const STRUCTURAL = /^(#|[-*+] |\d+[.)] |\||>|```)/;
const MONTH_YEAR = /^(january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{4}$/i;
const parts = (line: string) => line.split(/\s*·\s*/).map((p) => p.trim()).filter(Boolean);

/**
 * "KNOWLEDGE BASE · KB-001 · READ FIRST" → "Read first". The page already says
 * it is the knowledge base and shows the code, so only the rest is kept, in
 * sentence case; a label that says nothing else is dropped.
 */
function coverLabel(line: string): string | null {
  const rest = parts(line).filter((p) => !/^knowledge base$/i.test(p) && !/^KB-\d+[A-Z]*$/i.test(p));
  return rest.length ? sentenceCaseShouting(rest.join(" · ")) : null;
}

/**
 * "Category: Agency Foundation · All Clients · June 2026" → "Category: Agency
 * Foundation". The audience and the date are already in the page header.
 */
function coverMeta(line: string): string | null {
  const kept = parts(line).filter((p) => !/^all clients$/i.test(p) && !MONTH_YEAR.test(p));
  return kept.length ? kept.join(" · ") : null;
}

/** "Marketing Psychology &" ≈ "marketing psychology and": for comparing a cover line with the page title. */
const plain = (s: string) => s.toLowerCase().replace(/[&+]/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();

/** The cover line says what the page title says, give or take a word at either end ("… Expert", "No Limits …"). */
function restates(line: string, title: string): boolean {
  const a = plain(line);
  const b = plain(title);
  if (!a || !b) return false;
  if (a === b) return true;
  const [short, long] = a.length < b.length ? [a, b] : [b, a];
  return short.split(" ").length >= 2 && (long.startsWith(`${short} `) || long.endsWith(` ${short}`));
}

const isShouting = (s: string) => {
  const letters = s.replace(/[^A-Za-z]/g, "");
  return letters.length >= 4 && letters === letters.toUpperCase();
};
/**
 * Pulls the cover lines off the top of a document: consecutive short, single-line
 * paragraphs before the first real paragraph or heading. Returns null when the
 * document does not open that way, so ordinary lessons render untouched.
 *
 * Knowledge base documents open with an ALL-CAPS label ("KNOWLEDGE BASE · KB-006
 * · CLIENT PLAYBOOK"); after one, the title line may have been read as a heading
 * and "·" lists of topics run longer. Lines that only repeat the page (`title`,
 * the code, the audience, the month) are left out; the body never has them.
 */
export function splitCover(md: string, title?: string): { cover: DocCover | null; body: string } {
  const blocks = md.split(/\n{2,}/);
  const opening = blocks.find((b) => b.trim())?.trim() || "";
  const labelled = !STRUCTURAL.test(opening) && isShouting(opening);
  const taken: string[] = [];
  for (const raw of blocks) {
    const block = raw.trim();
    if (!block) { taken.push(""); continue; }
    const count = taken.filter(Boolean).length;
    // Title and subtitle lines right under the label, read as "### …" headings.
    const titleHeading = labelled && count >= 1 && count <= 3 && /^### /.test(block) && !block.includes("·");
    if (block.includes("\n") || (STRUCTURAL.test(block) && !titleHeading) || block.length > (labelled && block.includes("·") ? 160 : 90) || count >= 8) break;
    taken.push(block);
  }
  const lines = taken.filter(Boolean);
  const rest = blocks.slice(taken.length).join("\n\n").trim();
  if (lines.length < 2 || !rest) return { cover: null, body: md };
  const strip = (s: string) => s.replace(/^### /, "").replace(/^\*\*(.*)\*\*$/, "$1").replace(/^_(.*)_$/, "$1");
  let eyebrow: string | null = null;
  let meta: string | null = null;
  const body = lines.map(strip);
  const firstLetters = body[0].replace(/[^A-Za-z]/g, "");
  if (firstLetters && firstLetters === firstLetters.toUpperCase()) eyebrow = coverLabel(body.shift()!);
  const metaAt = body.findIndex((l) => /^(category|updated|owner|version)\s*:/i.test(l));
  if (metaAt >= 0) meta = coverMeta(body.splice(metaAt, 1)[0]);
  // A line Word broke in two ("Attribution, GTM, and" / "Schema Standards") is one line.
  for (let i = 0; i < body.length - 1; ) {
    const [a, b] = [body[i], body[i + 1]];
    if (!a.includes("·") && !b.includes("·") && (/(,|&|\+|:|\band|\bor)$/.test(a) || /^[a-z]/.test(b))) body.splice(i, 2, `${a} ${b}`);
    else i++;
  }
  // The page shows its title in large type just above, so a cover title saying the same goes,
  // and what is left under it are subtitle lines. A "·" list of topics is never a title.
  // Exact matches first, so a subtitle under an exact title line is kept.
  const pair = body.length > 1 && !body[1].includes("·") && body[1].split(/\s+/).length <= 6 ? `${body[0]} ${body[1]}` : null;
  let restated = true;
  if (title && body.length && plain(body[0]) === plain(title)) body.shift();
  else if (title && pair && restates(pair, title)) body.splice(0, 2);
  else if (title && body.length && restates(body[0], title)) body.shift();
  else restated = false;
  const coverTitle = !restated && body.length && !body[0].includes("·") ? body.shift()! : null;
  if (!eyebrow && !coverTitle && !body.length && !meta) return { cover: null, body: rest };
  return { cover: { eyebrow, title: coverTitle, lines: body, meta }, body: rest };
}

/**
 * The editor's view of a document's opening lines (one string per top-level block, "" for an
 * empty one): for each, the text the reader shows in its place, or null to show it as written.
 * "KNOWLEDGE BASE · KB-001 · READ FIRST" → "Read first"; "Category: SOPs · All Clients ·
 * June 2026" → "Category: SOPs". Display only: the saved text keeps every word.
 */
export function coverDisplay(lines: string[]): (string | null)[] {
  const first = lines.findIndex((l) => l.trim());
  const several = lines.filter((l) => l.trim()).length >= 2;
  return lines.map((raw, i) => {
    const line = raw.trim();
    if (!several || !line) return null;
    const shown = i === first && isShouting(line) ? coverLabel(line)
      : /^(category|updated|owner|version)\s*:/i.test(line) ? coverMeta(line)
      : null;
    return shown && shown !== line ? shown : null;
  });
}

/** Stable, readable ids for headings ("The Founding Context" → "the-founding-context"), unique within a page. */
export function slugger() {
  const seen = new Map<string, number>();
  return (text: string) => {
    const base = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "section";
    const n = seen.get(base) || 0;
    seen.set(base, n + 1);
    return n ? `${base}-${n + 1}` : base;
  };
}
